import FileDownload from 'js-file-download';
import { ChangeEvent, ComponentType, useRef, useState } from 'react';
import styled from 'styled-components';

import {
    ContractPriceListMessage,
    ContractPriceListStatus,
    PriceListActivationMode,
} from '../../../../types/ContractMessage';
import { DropdownMenu, DropdownMenuOption } from '../../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { Tag, TagProps } from '../../../cdl/Tag/Tag';
import { Text } from '../../../cdl/Text/Text';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { downloadContractPriceList, downloadNormalizedPriceList } from '../../../common/api/clients/contracts.api';
import { uploadGroupFile } from '../../../common/api/clients/fileManagement.api';
import { extractFileName } from '../../../common/helpers/extractFileName.helper';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconDownload } from '../../../common/icons/cdl/Download';
import { IconFile } from '../../../common/icons/cdl/File';
import { IconFileTypeCsv } from '../../../common/icons/cdl/FileTypeCsv';
import { IconFileTypeDoc } from '../../../common/icons/cdl/FileTypeDoc';
import { IconFileTypeDocx } from '../../../common/icons/cdl/FileTypeDocx';
import { IconFileTypeJpg } from '../../../common/icons/cdl/FileTypeJpg';
import { IconFileTypePdf } from '../../../common/icons/cdl/FileTypePdf';
import { IconFileTypePng } from '../../../common/icons/cdl/FileTypePng';
import { IconFileTypeXls } from '../../../common/icons/cdl/FileTypeXls';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { ContractEditPriceListModal } from '../../../contracts/detail/components/ContractEditPriceListModal';
import { useContractNormalizedFileUpload } from '../../../contracts/detail/hooks/useContractNormalizedFileUpload';
import { useContractPriceListActivate } from '../../../contracts/detail/hooks/useContractPriceListActivate';
import { useContractPriceListDeactivate } from '../../../contracts/detail/hooks/useContractPriceListDeactivate';
import { useContractPriceListDelete } from '../../../contracts/detail/hooks/useContractPriceListDelete';
import { useContractPriceListUpdate } from '../../../contracts/detail/hooks/useContractPriceListUpdate';
import { PRICE_LIST_MIN_WIDTH } from '../../../contracts/detail/priceList.constants';

const RowContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: ${theme.space[40]};
    width: 100%;
    min-width: ${PRICE_LIST_MIN_WIDTH}px;
    height: 40px;
    padding: ${theme.space[4]} ${theme.space[12]};
    border: 1px solid ${theme.colors.border.subtle};
    border-radius: ${theme.radii[8]};
    background: ${theme.colors.background.subtle};
`;

const FileMeta = styled.div`
    display: flex;
    align-items: center;
    gap: ${theme.space[8]};
    width: fit-content;
    max-width: 100%;
    flex: 0 1 auto;
    min-width: 0;
`;

const FileNameContainer = styled.div`
    flex: 1 1 100px;
    min-width: 100px;
    overflow: hidden;
`;

const FileName = styled(Text)`
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ValidityLabel = styled(Text)`
    flex-shrink: 0;
    white-space: nowrap;
`;

type FileIcon = ComponentType<{ width?: number; height?: number }>;

const mutedFileColor = theme.colors.foreground.muted;

const fileIconsByExtension: Record<string, { Icon: FileIcon; color: string }> = {
    csv: { Icon: IconFileTypeCsv, color: mutedFileColor },
    doc: { Icon: IconFileTypeDoc, color: '#2B579A' },
    docx: { Icon: IconFileTypeDocx, color: '#2B579A' },
    jpeg: { Icon: IconFileTypeJpg, color: mutedFileColor },
    jpg: { Icon: IconFileTypeJpg, color: mutedFileColor },
    pdf: { Icon: IconFileTypePdf, color: '#ED2224' },
    png: { Icon: IconFileTypePng, color: mutedFileColor },
    xls: { Icon: IconFileTypeXls, color: '#1D6F42' },
    xlsx: { Icon: IconFileTypeXls, color: '#1D6F42' },
};

const getFileIcon = (fileName?: string) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();

    if (!extension) {
        return { Icon: IconFile, color: theme.colors.foreground.muted };
    }

    return fileIconsByExtension[extension] ?? { Icon: IconFile, color: theme.colors.foreground.muted };
};

const getValidityLabel = (priceList: ContractPriceListMessage) => {
    const formattedValidFrom = formatDate({ date: priceList.validFrom, timeZone: 'UTC' });

    if (!priceList.validUntil) {
        return `${formattedValidFrom} ${Characters.RIGHTWARDS_ARROW} ${translate('contracts.priceLists.endOfContract')}`;
    }

    return `${formattedValidFrom} ${Characters.RIGHTWARDS_ARROW} ${formatDate({ date: priceList.validUntil, timeZone: 'UTC' })}`;
};

const getStatusTag = (status: ContractPriceListStatus): { label: string; variant: TagProps['variant'] } | null => {
    switch (status) {
        case ContractPriceListStatus.ACTIVE:
            return {
                label: translate('contracts.priceLists.status.active'),
                variant: 'accent',
            };
        case ContractPriceListStatus.EXPIRED:
            return {
                label: translate('contracts.priceLists.status.expired'),
                variant: 'default',
            };
        case ContractPriceListStatus.PROCESSING:
            return {
                label: translate('contracts.priceLists.status.processing'),
                variant: 'attention',
            };
        default:
            return null;
    }
};

interface PriceListRowProps {
    contractId: string;
    customerGroupId?: string;
    priceListActivationMode?: PriceListActivationMode;
    priceList: ContractPriceListMessage;
}

export const PriceListRow = ({
    contractId,
    customerGroupId,
    priceList,
    priceListActivationMode,
}: PriceListRowProps) => {
    const role = useRole();
    const { addErrorToast, addLoadingToast } = useToasts();
    const [isDownloading, setIsDownloading] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const activatePriceListMutation = useContractPriceListActivate();
    const deactivatePriceListMutation = useContractPriceListDeactivate();
    const deletePriceListMutation = useContractPriceListDelete();
    const updatePriceListMutation = useContractPriceListUpdate();
    const normalizedFileUploadMutation = useContractNormalizedFileUpload();
    const { Icon, color } = getFileIcon(priceList.fileName);
    const statusTag = getStatusTag(priceList.status);
    const updatedAt = priceList.dateUpdated ?? priceList.dateCreated;
    const isExpired = priceList.status === ContractPriceListStatus.EXPIRED;
    const isScheduledForApplication =
        priceList.activeOnApproval && priceList.status === ContractPriceListStatus.PROCESSING;
    const isManualMode = priceListActivationMode === PriceListActivationMode.MANUAL;
    const isPrimaryActionDisabled =
        !isManualMode ||
        deletePriceListMutation.isPending ||
        updatePriceListMutation.isPending ||
        activatePriceListMutation.isPending ||
        deactivatePriceListMutation.isPending;
    const fileNameColor = isExpired ? 'foreground.disabled' : 'foreground.default';
    const metaColor = isExpired ? 'foreground.disabled' : 'foreground.muted';
    const fileIconColor = isExpired ? theme.colors.foreground.disabled : color;

    const downloadPriceList = async () => {
        setIsDownloading(true);

        try {
            const response = await downloadContractPriceList(contractId, priceList.id);
            const fallbackFileName = priceList.fileName ?? translate('contracts.priceLists.fileNameFallback');

            FileDownload(
                response.data,
                extractFileName(response.headers['content-disposition'] || '', fallbackFileName)
            );
        } catch {
            addErrorToast(translate('global.genericToastError'));
        } finally {
            setIsDownloading(false);
        }
    };
    const downloadNormalized = async () => {
        setIsDownloading(true);

        try {
            const response = await downloadNormalizedPriceList(contractId, priceList.id);
            const fallbackFileName = priceList.fileName ?? translate('contracts.priceLists.fileNameFallback');

            FileDownload(
                response.data,
                extractFileName(response.headers['content-disposition'] || '', fallbackFileName)
            );
        } catch {
            addErrorToast(translate('global.genericToastError'));
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !customerGroupId) return;

        event.target.value = '';

        await addLoadingToast(
            async () => {
                const groupFileResponse = await uploadGroupFile(file, customerGroupId, () => {});
                await normalizedFileUploadMutation.mutateAsync({
                    contractId,
                    priceListId: priceList.id,
                    normalizedFileId: groupFileResponse.id,
                });
            },
            translate('contracts.priceLists.actions.uploadLoading'),
            translate('contracts.priceLists.actions.uploadSuccess'),
            translate('contracts.priceLists.actions.uploadError')
        );
    };

    const deletePriceList = async () => {
        await addLoadingToast(
            () =>
                deletePriceListMutation.mutateAsync({
                    contractId,
                    priceListId: priceList.id,
                }),
            translate('contracts.priceLists.actions.deleteLoading'),
            translate('contracts.priceLists.actions.deleteSuccess'),
            translate('contracts.priceLists.actions.deleteError')
        );
    };
    const updateApplication = async (activeOnApproval: boolean) => {
        await addLoadingToast(
            () =>
                updatePriceListMutation.mutateAsync({
                    contractId,
                    priceListId: priceList.id,
                    priceList: {
                        originalFileId: priceList.originalFileId,
                        validFrom: priceList.validFrom,
                        validUntil: priceList.validUntil,
                        activeOnApproval,
                    },
                }),
            activeOnApproval
                ? translate('contracts.priceLists.actions.applyLoading')
                : translate('contracts.priceLists.actions.cancelApplicationLoading'),
            activeOnApproval
                ? translate('contracts.priceLists.actions.applySuccess')
                : translate('contracts.priceLists.actions.cancelApplicationSuccess'),
            activeOnApproval
                ? translate('contracts.priceLists.actions.applyError')
                : translate('contracts.priceLists.actions.cancelApplicationError')
        );
    };
    const activatePriceList = async () => {
        await addLoadingToast(
            () => activatePriceListMutation.mutateAsync({ contractId, priceListId: priceList.id }),
            translate('contracts.priceLists.actions.applyLoading'),
            translate('contracts.priceLists.actions.applySuccess'),
            translate('contracts.priceLists.actions.applyError')
        );
    };
    const deactivatePriceList = async () => {
        await addLoadingToast(
            () => deactivatePriceListMutation.mutateAsync({ contractId, priceListId: priceList.id }),
            translate('contracts.priceLists.actions.stopApplyingLoading'),
            translate('contracts.priceLists.actions.stopApplyingSuccess'),
            translate('contracts.priceLists.actions.stopApplyingError')
        );
    };

    const getPrimaryAction = (): DropdownMenuOption | null => {
        if (priceList.status === ContractPriceListStatus.EXPIRED) {
            return null;
        }

        if (priceList.status === ContractPriceListStatus.PROCESSING) {
            return {
                label: isScheduledForApplication
                    ? translate('contracts.priceLists.actions.cancelApplication')
                    : translate('contracts.priceLists.actions.applyToPricing'),
                description: isManualMode
                    ? undefined
                    : translate('contracts.priceLists.actions.manualModeOnlyDescription'),
                disabled: isPrimaryActionDisabled,
                onSelect: () => updateApplication(!priceList.activeOnApproval),
            };
        }

        if (priceList.status === ContractPriceListStatus.ACTIVE) {
            return {
                label: translate('contracts.priceLists.actions.stopApplying'),
                description: isManualMode
                    ? undefined
                    : translate('contracts.priceLists.actions.manualModeOnlyDescription'),
                disabled: isPrimaryActionDisabled,
                onSelect: () => deactivatePriceList(),
            };
        }

        return {
            label: translate('contracts.priceLists.actions.applyToPricing'),
            description: isManualMode ? undefined : translate('contracts.priceLists.actions.manualModeOnlyDescription'),
            disabled: isPrimaryActionDisabled,
            onSelect: () => activatePriceList(),
        };
    };

    const primaryAction = getPrimaryAction();
    const actionOptions: DropdownMenuOption[] = [
        ...(primaryAction ? [primaryAction] : []),
        ...(role.isSuperAdmin()
            ? [
                  {
                      label: priceList.normalizedFileId
                          ? translate('contracts.priceLists.actions.reupload')
                          : translate('contracts.priceLists.actions.upload'),
                      disabled: normalizedFileUploadMutation.isPending,
                      onSelect: () => fileInputRef.current?.click(),
                  },
                  ...(priceList.normalizedFileId
                      ? [
                            {
                                label: translate('contracts.priceLists.actions.download'),
                                disabled: isDownloading,
                                onSelect: () => downloadNormalized(),
                            },
                        ]
                      : []),
              ]
            : []),
        {
            label: translate('contracts.priceLists.edit'),
            disabled:
                updatePriceListMutation.isPending ||
                deletePriceListMutation.isPending ||
                activatePriceListMutation.isPending ||
                deactivatePriceListMutation.isPending,
            onSelect: () => setIsEditModalOpen(true),
        },
        {
            label: translate('contracts.priceLists.actions.delete'),
            disabled:
                deletePriceListMutation.isPending ||
                updatePriceListMutation.isPending ||
                activatePriceListMutation.isPending ||
                deactivatePriceListMutation.isPending,
            onSelect: () => deletePriceList(),
            destructive: true,
        },
    ];

    return (
        <>
            <RowContainer>
                <Box display="flex" alignItems="center" gap={8} flex="1 1 auto" minWidth={0}>
                    <Icon width={20} height={20} style={{ color: fileIconColor, flexShrink: 0 }} />

                    <FileMeta>
                        <FileNameContainer>
                            <FileName variant="extraSmall" fontWeight={600} color={fileNameColor}>
                                {priceList.fileName ?? translate('contracts.priceLists.fileNameFallback')}
                            </FileName>
                        </FileNameContainer>

                        <Box display="flex" alignItems="center" gap={8} flexShrink={0}>
                            <ValidityLabel variant="extraSmall" color={metaColor}>
                                {getValidityLabel(priceList)}
                            </ValidityLabel>

                            {statusTag ? (
                                <Box flexShrink={0}>
                                    <Tag variant={statusTag.variant}>{statusTag.label}</Tag>
                                </Box>
                            ) : null}

                            {isScheduledForApplication ? (
                                <Box display="flex" alignItems="center" gap={8} flexShrink={0}>
                                    <Text variant="extraSmall" color={metaColor}>
                                        {Characters.RIGHTWARDS_ARROW}
                                    </Text>
                                    <Tag variant="accent">{translate('contracts.priceLists.status.active')}</Tag>
                                </Box>
                            ) : null}
                        </Box>
                    </FileMeta>
                </Box>

                <Box display="flex" alignItems="center" gap={8} flexShrink={0}>
                    {role.isSuperAdmin() && priceList.normalizedFileId ? (
                        <Tag variant="positive">{translate('contracts.priceLists.normalized')}</Tag>
                    ) : null}

                    {updatedAt ? (
                        <Text variant="extraSmall" color={metaColor}>
                            {formatDate({ date: updatedAt })}
                        </Text>
                    ) : null}

                    <IconButton
                        type="button"
                        Icon={IconDownload}
                        emphasis="transparent"
                        tooltipLabel={translate('contracts.priceLists.download')}
                        onClick={downloadPriceList}
                        disabled={isDownloading}
                    />

                    {role.hasAnyAdminRights() ? <DropdownMenu align="end" options={actionOptions} /> : null}
                </Box>
            </RowContainer>

            {role.isSuperAdmin() ? (
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xls,.xlsx,.pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={handleFileSelected}
                />
            ) : null}

            {isEditModalOpen ? (
                <ContractEditPriceListModal
                    activationMode={priceListActivationMode}
                    contractId={contractId}
                    isOpen
                    onDismiss={() => setIsEditModalOpen(false)}
                    priceList={priceList}
                />
            ) : null}
        </>
    );
};
